
import Vue from 'vue'
import CashControlType from '@/Models/Static/Business/CashControlType'
import PaymentMethodType from '@/Models/Static/PaymentMethod'

const CASH = 'cash'
const CARD = 'card'
const OTHER = 'other'

const FULL_CASH_TILL = {
  '50000': 0,
  '20000': 0,
  '10000': 0,
  '5000': 0,
  '2000': 0,
  '1000': 0,
  '500': 0,
  '200': 0,
  '100': 0,
  '50': 0,
  '20': 0,
  '10': 0,
  '5': 0,
  '2': 0,
  '1': 0,
}

const SIMPLE_CASH_TILL = {
  '100': 0,
  '1': 0,
}

const state = () => ({
  /**
   * Open/close turn form dialog visibility.
   * @type Boolean
   */
  show: false,

  /**
   * Available payment methods.
   * @type Array
   */
  paymentMethods: [],

  /**
   * Selected payment method index.
   * @type Number
   */
  selectedPaymentMethod: 0,

  /**
   * Selected money index.
   * @type Number
   */
  selectedMoney: 0,

  /**
   * This is the data that will be sent to the server.
   * @type Object
   */
  tills: {},

  /**
   * Total turn amount.
   * @type Number
   */
  total: 0,
})

const getters = {
  fullCashControl: (state, getters, rootState, rootGetters) => rootGetters['rummage/business']('cashControlType') == CashControlType.FULL,
  paymentMethod: state => state.paymentMethods[state.selectedPaymentMethod],
  isCash: (state, getters) => PaymentMethodType.isCash(getters.paymentMethod?.id),
  isCard: (state, getters) => PaymentMethodType.isCard(getters.paymentMethod?.id),
  isOther: (state, getters) => PaymentMethodType.isOther(getters.paymentMethod?.id),
  cashTill: state => state.tills[CASH] || {},
  monies: (state, getters) => Object.keys(getters.cashTill).map(v => parseInt(v)),
  amount: (state, getters) => {
    if (getters.isCash) {
      return getters.fullCashControl
        ? Object.values(getters.cashTill)?.[state.selectedMoney]?.toString() ?? '0'
        : Number(100 * (getters.cashTill['100'] ?? 0) + (getters.cashTill['1'] ?? 0)).toString()
    }
    return Math.round((getters.isCard ? state.tills[CARD] ?? 0 : state.tills[OTHER]?.[getters.paymentMethod?.id] ?? 0) * 100).toString()
  },
}

const mutations = {
  show (state) {
    state.show = true
  },
  hide (state) {
    state.show = false
  },
  paymentMethods (state, v) {
    state.paymentMethods = Array.isArray(v) ? v : Array.of(v)
  },
  selectedPaymentMethod (state, v) {
    state.selectedPaymentMethod = v
  },
  selectedMoney (state, v) {
    state.selectedMoney = v
  },
  till (state, { type, value }) {
    Vue.set(state.tills, type, value)
  },
  amount (state, { type, index, value }) {
    if (index === undefined) {
      state.tills[type] = value
    } else {
      state.tills[type][index] = value
    }
  },
  resetTotal (state) {
    state.total = 0
  },
  calculateTotal (state) {
    let total = state.tills[CARD] || 0
    total += Object.entries(state.tills[CASH] || {})
      .map(([money, amount]) => (parseInt(money) / 100) * amount)
      .sum()
    total += Object.values(state.tills[OTHER] || {}).sum()

    state.total = total
  },
}

const actions = {
  open ({ state, commit, getters, rootState, rootGetters }) {
    const openingTurn = ! rootState.cashControl.turn
    const getter = openingTurn ? 'cashPayment' : 'endTillPaymentMethods'
    commit('paymentMethods', rootGetters[`rummage/${getter}`])
    commit('selectedPaymentMethod', 0)
    commit('selectedMoney', 0)
    commit('till', { type: CASH, value: getters['fullCashControl'] ? {...FULL_CASH_TILL} : {...SIMPLE_CASH_TILL} })
    commit('till', { type: CARD, value: state.paymentMethods.some(o => PaymentMethodType.isCard(o.id)) ? 0 : null })
    commit('till', { type: OTHER, value: state.paymentMethods.some(o => PaymentMethodType.isOther(o.id))
      ? Object.fromEntries(state.paymentMethods.filter(o => PaymentMethodType.isOther(o.id)).map(o => [o.id, 0]))
      : null
    })
    commit('resetTotal')
    commit('show')
  },
  updateAmount ({ state, commit, getters }, amount) {
    if (getters.isCard) {
      commit('amount', {
        type: CARD,
        value: Math.roundToCurrency(amount / 100),
      })
    } else if (getters.isOther) {
      commit('amount', {
        type: OTHER,
        index: getters.paymentMethod?.id,
        value: Math.roundToCurrency(amount / 100),
      })
    } else if (getters.fullCashControl) {
      commit('amount', {
        type: CASH,
        index: getters.monies[state.selectedMoney],
        value: amount,
      })
    } else { // simpleCashControl
      const integer = Math.floor(amount / 100)
      const decimal = amount - integer * 100
      commit('amount', { type: CASH, index: '100', value: integer })
      commit('amount', { type: CASH, index: '1', value: decimal })
    }
    commit('calculateTotal')
  },
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
